import {
  BeaconMessageType,
  generateBeaconMessage,
  type BeaconShareCodeCreatedPayload,
  type BeaconShareCodeReclaimedPayload,
  type BeaconShareCodeReclaimFailedPayload,
} from '@playcastdotio/Messaging';
import type { IRealtimeService } from '../../../services/realtime/IRealtimeService';
import type { CoordinatorEvent } from '../../_types/events';
import { CoordinatorEventImpl } from '../../_types/events';
import { isDebugWebrtcLoggingEnabled } from '@playcastdotio/PeerCore';

export type ShareCodeManagerDeps = {
  realtimeService: IRealtimeService;
  getMachineId: () => string;
  getAuthId: () => string;
  getMaxPlayers: () => number;
  getPlayerBaseUrl: () => string;
  getStreamTitle: () => string;
};

export class ShareCodeManager {
  private shareCode: string | null = null;
  private deps: ShareCodeManagerDeps;
  private isStreamActive = false;

  public readonly events = {
    shareCodeReady: new CoordinatorEventImpl<{ shareCode: string; shareLink: string }>(),
    shareCodeEnded: new CoordinatorEventImpl<void>(),
  };

  constructor(deps: ShareCodeManagerDeps) {
    this.deps = deps;
  }

  getShareCode(): string | null {
    return this.shareCode;
  }

  getShareLink(): string | null {
    if (!this.shareCode) return null;
    const base = `${this.deps.getPlayerBaseUrl()}/j/${this.shareCode}`;
    // DEBUG BUILD ONLY — every invite a debug host hands out carries ?debug so the
    // player opens with the WebRTC debug gates armed without anyone remembering to
    // append the param by hand. Production builds constant-fold this away.
    if (isDebugWebrtcLoggingEnabled()) {
      return `${base}${base.includes('?') ? '&' : '?'}debug`;
    }
    return base;
  }

  onStreamStarted(): void {
    this.isStreamActive = true;
    this.requestNewShareCode();
  }

  onStreamStopped(): void {
    this.isStreamActive = false;
    if (this.shareCode) {
      this.sendEndMessage(this.shareCode);
    }
    this.clearShareCode();
  }

  onReconnected(): void {
    if (!this.isStreamActive) return;

    if (this.shareCode) {
      this.requestReclaim(this.shareCode);
    } else {
      this.requestNewShareCode();
    }
  }

  handleShareCodeCreated(payload: BeaconShareCodeCreatedPayload): void {
    this.shareCode = payload.shareCode;
    const shareLink = this.getShareLink()!;
    console.log(`[ShareCodeManager] Share code created: ${this.shareCode}`);
    this.events.shareCodeReady.emit({ shareCode: this.shareCode, shareLink });
  }

  handleShareCodeReclaimed(payload: BeaconShareCodeReclaimedPayload): void {
    this.shareCode = payload.shareCode;
    const shareLink = this.getShareLink()!;
    console.log(`[ShareCodeManager] Share code reclaimed: ${this.shareCode}`);
    this.events.shareCodeReady.emit({ shareCode: this.shareCode, shareLink });
  }

  handleShareCodeReclaimFailed(_payload: BeaconShareCodeReclaimFailedPayload): void {
    console.warn('[ShareCodeManager] Reclaim failed, requesting new code');
    this.shareCode = null;
    if (this.isStreamActive) {
      this.requestNewShareCode();
    }
  }

  private requestNewShareCode(): void {
    const title = this.deps.getStreamTitle();
    const message = generateBeaconMessage(BeaconMessageType.BEACON_SHARE_CODE_CREATE, {
      machineId: this.deps.getMachineId(),
      authId: this.deps.getAuthId(),
      maxPlayers: this.deps.getMaxPlayers(),
      streamTitle: title || undefined,
    });
    this.deps.realtimeService.sendMessage(message);
  }

  private requestReclaim(shareCode: string): void {
    const message = generateBeaconMessage(BeaconMessageType.BEACON_SHARE_CODE_RECLAIM, {
      shareCode,
      machineId: this.deps.getMachineId(),
      authId: this.deps.getAuthId(),
    });
    this.deps.realtimeService.sendMessage(message);
  }

  private sendEndMessage(shareCode: string): void {
    const message = generateBeaconMessage(BeaconMessageType.BEACON_SHARE_CODE_END, {
      shareCode,
    });
    this.deps.realtimeService.sendMessage(message);
  }

  private clearShareCode(): void {
    this.shareCode = null;
    this.events.shareCodeEnded.emit();
  }
}
